import React, { Fragment, PureComponent } from 'react';
import { createPortal } from 'react-dom';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { get, throttle, debounce } from 'lodash';
import { Row, Col, Button, Alert } from 'reactstrap';
import generate from 'shortid';

import {
  GET_CREATE_TEMPLATE_URL, getCompanies, getEditTemplateUrl, getEsignDocs, getCompany,
  toggleDrawer, GET_ESIGN_DOCS, setSelectedDocument, getDocumentPreviewUrl,
  displayToaster, DELETE_COMPANY_DOCUMENT, LOADING_PROP_TYPE,
  CREATE_SIGNATURE_REQUEST, clearLoadingState, getTenantEsignatureData,
} from '../../modules';
import { CompanyDocumentsTable, NoContent, Spinner, SearchBar, SendForSignatures } from '../../components';
import { EditTemplate, CreateTemplate } from '..';
import { FILTER_TABLE_DEBOUNCE, FETCH_TABLE_SEARCH_DEBOUNCE } from '../../constants';
import { esignProductTierNames } from '../../constants/esignProductTierNames';


class CompanyDocuments extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      target: document.getElementById('company-documents-ui'),
      nextPage: '',
      mode: '',
      selectedCompanyId: get(window, 'asure.appSettings.selectedCompany.companyId'),
      paginating: true,
      docFilter: '',
      filtering: false,
      searching: false,
      isModalOpen: false,
      latestSearch: '',
      fetchActive: '',
      unfilteredNextPage: '',
    };
    this.throttledScroll = throttle(this.handleScroll.bind(this), 500);
    this.showSendSignatureModal = this.showSendSignatureModal.bind(this);
    this.editTemplate = this.editTemplate.bind(this);
  }

  componentDidMount() {
    const { target, nextPage: next, selectedCompanyId } = this.state;
    window.addEventListener('scroll', this.throttledScroll);
    if (target) {
      this.props.getTenantEsignatureData();
      // NOTE: if the user has selected a company through ADHR 2.0, we prevent the user
      // from changing to a different company by disabling the dropdown. In this case,
      // we don't need to get a list of available companies.
      if (!selectedCompanyId) {
        this.props.getCompanies();
      } else {
        this.props.getEsignDocs(next);
        this.props.getCompany(selectedCompanyId);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.selectedCompanyId && this.state.selectedCompanyId) {
      this.props.getEsignDocs();
    }
    if (this.props.loading[DELETE_COMPANY_DOCUMENT] === 'complete' && prevProps.loading[DELETE_COMPANY_DOCUMENT] === 'loading') {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_DELETED" />,
        'app',
        5000,
        true,
      ]);
    }
    if (prevProps.loading[CREATE_SIGNATURE_REQUEST] === 'loading' && this.props.loading[CREATE_SIGNATURE_REQUEST] === 'complete') {
      const { selectedDocument: { title }, sentSignatureRequests } = this.props;
      const signatureRequestCount = sentSignatureRequests.length;
      this.props.displayToaster([
        'success',
        <FormattedMessage
          id="SIGNATURE_REQUEST_SENT_TO_EMPLOYEES"
          values={{
            title,
            employeeCount: signatureRequestCount,
            plural: signatureRequestCount > 1 ? 's' : '',
          }}
          defaultMessage="SIGNATURE_REQUEST_SENT"
        />,
        'app',
        5000,
        true,
      ]);
      this.handleSendSignatureModal();
    }
    if (this.state.fetchActive !== this.state.latestSearch &&
        this.props.loading.currentRequests[GET_ESIGN_DOCS].includes(this.state.latestSearch)) {
      this.setFetchActive();
    }
    if (this.state.filtering &&
        this.state.fetchActive === this.state.latestSearch &&
        !this.props.loading.currentRequests[GET_ESIGN_DOCS].includes(this.state.latestSearch)) {
      this.unsetFiltering();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.throttledScroll);
  }

  setFetchActive() {
    this.setState({ fetchActive: this.state.latestSearch });
  }

  toggleSendSignatureModal() {
    this.setState({ mode: 'sendSignature' });
    this.setState({ isModalOpen: !this.state.isModalOpen });
  }

  handleSendSignatureModal() {
    this.toggleSendSignatureModal();
    this.props.clearLoadingState(CREATE_SIGNATURE_REQUEST);
  }

  showSendSignatureModal(templateId) {
    const { docs: { results: documents } } = this.props;
    const document = documents.filter(doc => doc.id === templateId)[0];

    this.setState({ mode: 'sendSignature' });
    this.setState({ isModalOpen: true }, () => {
      this.props.setSelectedDocument(document);
    });
  }

  toggleCreateTemplateModal(close) {
    this.setState({ mode: 'create' });
    this.setState({ isModalOpen: !close });
  }

  unsetFiltering() {
    this.setState({ filtering: false });
  }

  editTemplate(templateId) {
    const { docs: { results: documents }, getEsignatureDocsLoading } = this.props;
    const document = documents.filter(doc => doc.id === templateId)[0];

    // Prevent edit functionality while we're loading data
    if (getEsignatureDocsLoading === 'loading') return;
    const { state } = this;
    this.setState({
      ...state,
      mode: 'edit',
    }, () => {
      this.props.setSelectedDocument(document);
      if (document.isHelloSignTemplate) {
        this.props.getEditTemplateUrl(templateId);
      } else {
        this.props.getDocumentPreviewUrl('company', document.id);
      }
      this.props.toggleDrawer(true);
    });
  }

  searchDocs(query, requestId) {
    if (query) {
      this.props.getEsignDocs('', query, requestId);
    } else {
      this.props.getEsignDocs(this.state.unfilteredNextPage);
    }
  }

  debounceSearchDocs = debounce(this.searchDocs, (FETCH_TABLE_SEARCH_DEBOUNCE - FILTER_TABLE_DEBOUNCE));

  handleSearch(query) {
    if (query !== this.state.docFilter) {
      const requestId = generate();
      this.setState({
        docFilter: query,
        paginating: true,
        searching: !!query,
        filtering: !!query,
        latestSearch: requestId,
      });
      this.debounceSearchDocs(query, requestId);
    }
  }

  debounceHandleSearch = debounce(this.handleSearch, FILTER_TABLE_DEBOUNCE);

  // Load more documents when the user has reached the end of the page
  handleScroll() {
    const { next } = this.props.docs;
    const unfilteredNext = this.state.docFilter ? this.state.unfilteredNext : next;
    if (!next) {
      this.setState({ paginating: false });
      return;
    }
    if (window.innerHeight + document.documentElement.scrollTop > document.documentElement.offsetHeight - 100) {
      this.setState(() => ({
        nextPage: next,
        paginating: true,
        searching: false,
        unfilteredNextPage: unfilteredNext,
      }), () => this.props.getEsignDocs(next));
    }
  }

  buildComponentContent() {
    const {
      loading,
      docs: { results: documents },
      companyEsignTierName,
      getEsignatureDocsLoading,
    } = this.props;
    const {
      selectedCompanyId,
      paginating,
      docFilter,
      filtering,
      searching,
    } = this.state;

    if (loading[GET_ESIGN_DOCS] === 'complete' && documents.length === 0) {
      return (
        <Fragment>
          <Col sm={12}>
            <NoContent
              heading="NO_DOCUMENTS_TO_DISPLAY"
              bodyText="ADD_A_DOCUMENT_TO_GET_STARTED_WITH_E_SIGNATURES"
              buttonText="ADD_DOCUMENT"
              onClick={() => this.toggleCreateTemplateModal(false)}
            />
          </Col>
        </Fragment>
      );
    } else if (!selectedCompanyId) {
      return (
        <Row>
          <Col sm={12}>
            <Alert color="primary">
              <h4><FormattedMessage id="SELECT_A_COMPANY" /></h4>
              <p><FormattedMessage id="SELECT_A_COMPANY_VERBIAGE" /></p>
            </Alert>
          </Col>
        </Row>
      );
    }
    return (
      <Fragment>
        <Row className="pb-3 docs-toolbar">
          <Col
            xs={{ size: 12 }}
            sm={{ size: 6 }}
            md={{ size: 5 }}
            lg={{ size: 4 }}
            xl={{ size: 3 }}
            className="text-left"
          >
            <SearchBar
              name="docFilter"
              placeholder="Search Company Documents"
              keyUpHandler={this.debounceHandleSearch.bind(this)}
              loading={getEsignatureDocsLoading === 'loading'}
              searching={searching}
              filtering={filtering}
            />
          </Col>
          <Col
            xs={{ size: 12, offset: 0 }}
            sm={{ size: 4, offset: 2 }}
            md={{ size: 3, offset: 4 }}
            lg={{ size: 3, offset: 5 }}
            xl={{ size: 2, offset: 7 }}
            className="text-right my-auto"
          >
            <Button
              onClick={() => this.toggleCreateTemplateModal(false)}
              disabled={loading[GET_CREATE_TEMPLATE_URL] === 'loading'}
              color="primary"
              block
            >
              <FormattedMessage id="ADD_DOCUMENT" />
            </Button>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <div className="bg-white border-grey border p-4 w-100 d-flex flex-column">
              <CompanyDocumentsTable
                data={documents}
                rowAction={this.editTemplate}
                key={documents}
                sendSignatureRequestsAction={this.showSendSignatureModal}
                companyEsignTierName={companyEsignTierName}
                getEsignatureDocsLoading={getEsignatureDocsLoading}
                docFilter={docFilter}
                filtering={filtering}
                searching={searching}
              />
            </div>
          </Col>
          <Col sm={12}>
            <div className="w-100 d-flex justify-content-center">
              <h6 className="d-flex justify-content-center flex-row p-3">
                {
                  paginating
                  && getEsignatureDocsLoading === 'loading'
                  && (
                    this.state.searching ?
                      null
                      :
                      <Fragment><Spinner className="mr-1" /><FormattedMessage id="LOADING_MORE_DOCUMENTS" />...</Fragment>
                  )
                }
              </h6>
            </div>
          </Col>
        </Row>
      </Fragment>
    );
  }

  render() {
    const { target, mode, selectedCompanyId } = this.state;
    let content;
    if (selectedCompanyId) {
      if (mode === 'edit') {
        content = <EditTemplate />;
      } else if (mode === 'sendSignature') {
        content = <SendForSignatures isOpen={this.state.isModalOpen} toggle={() => this.toggleSendSignatureModal()} />;
      } else {
        content =
          (<CreateTemplate
            isOpen={this.state.isModalOpen}
            toggle={() => this.toggleCreateTemplateModal(true)}
          />);
      }
    }
    const component = (
      <div className="pt-4 h-100">
        {content}
        <Row>
          <Col xs={12}>
            <h1 className="h3"><FormattedMessage id="COMPANY_DOCUMENTS" /></h1>
          </Col>
        </Row>
        {this.buildComponentContent()}
      </div>
    );
    if (target) {
      return createPortal(component, target);
    }
    return null;
  }
}

CompanyDocuments.defaultProps = {
  toggleDrawer: null,
  loading: {},
  selectedDocument: {},
  sentSignatureRequests: [],
  getEsignatureDocsLoading: '',
};

CompanyDocuments.propTypes = {
  getTenantEsignatureData: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func,
  loading: LOADING_PROP_TYPE,
  getEsignatureDocsLoading: PropTypes.string,
  docs: PropTypes.shape({
    next: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string,
      title: PropTypes.string,
      uploadDate: PropTypes.string,
      uploadedBy: PropTypes.string,
      category: PropTypes.string,
      isEsignatureDocument: PropTypes.bool,
      isHelloSignTemplate: PropTypes.bool,
      isPublishedToEmployee: PropTypes.bool,
    })),
  }).isRequired,
  selectedDocument: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  sentSignatureRequests: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
  getCompanies: PropTypes.func.isRequired,
  getCompany: PropTypes.func.isRequired,
  getEsignDocs: PropTypes.func.isRequired,
  setSelectedDocument: PropTypes.func.isRequired,
  getDocumentPreviewUrl: PropTypes.func.isRequired,
  getEditTemplateUrl: PropTypes.func.isRequired,
  displayToaster: PropTypes.func.isRequired,
  clearLoadingState: PropTypes.func.isRequired,
  companyEsignTierName: PropTypes.string.isRequired,
};

export default connect(
  (state) => {
    const {
      resources: {
        docs: {
          esignDocs,
          selectedDocument,
        },
        signatureRequest: {
          sentSignatureRequests,
        },
        companies: {
          results,
          selectedCompany: {
            esignatureProductTier: {
              name: companyEsignTierName = esignProductTierNames.SIMPLESIGN,
            },
          },
        },
      },
      appState: {
        loading,
      },
    } = state;
    return {
      getEsignatureDocsLoading: loading[GET_ESIGN_DOCS],
      loading,
      docs: esignDocs,
      companies: results,
      selectedDocument,
      sentSignatureRequests,
      companyEsignTierName,
    };
  },
  {
    getEsignDocs,
    getCompanies,
    getCompany,
    toggleDrawer: open => toggleDrawer(open),
    getEditTemplateUrl: templateId => getEditTemplateUrl(templateId),
    setSelectedDocument: document => setSelectedDocument(document),
    getDocumentPreviewUrl,
    displayToaster: args => displayToaster(...args),
    clearLoadingState: action => clearLoadingState(action),
    getTenantEsignatureData: formatMessage => getTenantEsignatureData(formatMessage),
  }
)(hot(module)(CompanyDocuments));



// WEBPACK FOOTER //
// ./src/containers/CompanyDocuments/CompanyDocuments.jsx